import React, { useState } from 'react';
import GenericDialog from 'src/components/GenericDialog';
import DynamicFormWidget from 'src/components/dynamic-widget/DynamicFormWidget';
import { DynamicField, DynamicFieldTypes } from 'src/components/dynamic-widget';
import { DataService } from 'src/services/data-service';
import { toast } from 'react-toastify';
import { Alert } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { showAlert } from 'src/redux/alert/alertReducer';
import { useAppDispatch } from 'src/redux/hooks';

interface Warehouse {
  id: string;
  Code: string;
  Name: string;
  Virtual: boolean;
  Location: string;
  Dimensions: string;
  IsDefault: boolean;
}

interface WarehouseDrawerProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  updateData?: Warehouse;
}

const WarehouseDrawer: React.FC<WarehouseDrawerProps> = ({
  open,
  onClose,
  onSuccess,
  updateData
}) => {
  const isEdit = Boolean(updateData);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const dispatch = useAppDispatch();

  // Build initial values for the form
  const initialValues = updateData
    ? {
        Code: updateData.Code || '',
        Name: updateData.Name || '',
        Virtual: updateData.Virtual ? 'true' : 'false',
        Location: updateData.Location || '',
        Dimensions: updateData.Dimensions || '',
        // IsDefault: updateData.IsDefault ? 'true' : 'false',
      }
    : {
        Code: '',
        Name: '',
        Virtual: '',
        Location: '',
        Dimensions: '',
        // IsDefault: '',
      };

  // Optional: handle Code uppercase on change
  const handleFieldChange = (name: string, value: any, allValues: Record<string, any>) => {
    if (name === 'Code' && typeof value === 'string') {
      // Force uppercase for Code
      allValues.Code = value.toUpperCase();
    }
  };

  const formConfig: Record<string, DynamicFieldTypes> = {
    Code: {
      type: DynamicField.TEXT,
      name: 'Code',
      title: 'Code',
      required: true,
      disabled: isEdit,
      helperText: null,
      value: '',
    },
    Name: {
      type: DynamicField.TEXT,
      name: 'Name',
      title: 'Name',
      required: true,
      disabled: false,
      value: '',
    },
    Virtual: {
      type: DynamicField.SELECT,
      name: 'Virtual',
      title: 'Virtual',
      required: true,
      items: { 'true': 'Yes', 'false': 'No' },
      disabled: false,
      clearable: true,
      value: '',
    },
    // IsDefault: {
    //   type: DynamicField.SELECT,
    //   name: 'IsDefault',
    //   title: 'Is Default',
    //   required: true,
    //   items: { 'true': 'Yes', 'false': 'No' },
    //   disabled: false,
    //   clearable: true,
    //   value: '',
    // },
    Location: {
      type: DynamicField.TEXT,
      name: 'Location',
      title: 'Location',
      required: true,
      disabled: false,
      value: '',
    },
    Dimensions: {
      type: DynamicField.TEXT,
      name: 'Dimensions',
      title: 'Dimensions',
      required: true,
      disabled: false,
      value: '',
    }
  };

  const formLayout = {
    groups: [
      {
        title: 'General Info',
        fields: ['Code', 'Name', 'Virtual'], // 'IsDefault' commented out
        columns: 2 as 2
      },
      {
        title: 'Location Info',
        fields: ['Location', 'Dimensions'],
        columns: 2 as 2
      }
    ]
  };

  const submit = async (data: Record<string, any>) => {
    setSubmitError(null);
    data.Virtual = data.Virtual === 'true' || data.Virtual === true;
    // data.IsDefault = data.IsDefault === 'true' || data.IsDefault === true;

    try {
      if (isEdit && updateData) {
        data.Id = updateData.id;
        const res = await DataService.put(`api/Warehouse/${data.Id}`, data);
        const body = await res.json();
        if (res.ok && body.success) {
          dispatch(showAlert({ message: 'Warehouse updated successfully!', type: 'success' }));
          onSuccess();
          return true;
        } else {
          dispatch(showAlert({ message: body.message || 'Failed to update warehouse', type: 'error' }));
          return false;
        }
      } else {
        const res = await DataService.post('api/Warehouse', data);
        const body = await res.json();
        if (res.ok && body.success) {
          dispatch(showAlert({ message: 'Warehouse Added successfully!', type: 'success' }));
          onSuccess();
          return true;
        } else {
          dispatch(showAlert({ message: body.message || 'Failed to update warehouse', type: 'error' }));
          return false;
        }
      }
    } catch {
      setSubmitError('An Error occurred!');
      return false;
    }
  };

  return (
<GenericDialog
disableAutoFocus
disableEnforceFocus
    title={isEdit ? 'Edit Warehouse' : 'Add Warehouse'}
    open={open}
    onClose={onClose}
    data-testid={isEdit ? 'edit-warehouse-dialog' : 'add-warehouse-dialog'}
  >
      {submitError && (
        <Alert
          severity="error"
          data-testid="submit-error-alert"
          onClose={() => setSubmitError(null)}
          sx={{ mb: 2 }}
        >
          {submitError}
        </Alert>
      )}

      <DynamicFormWidget
        autoFocusFirst={true}
        drawerMode
        title=""
        fields={formConfig}
        layout={formLayout}
        initialValues={initialValues}
        onFieldChange={handleFieldChange}
        onSubmit={submit}
        data-testid="warehouse-form"
      />
    </GenericDialog>
  );
};

export default WarehouseDrawer;
